import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import apiConfig from '../../config/apiConfig.js';

interface UserProfile {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  profileImage: string;
  myReferralCode: string;
}

interface SubscriptionInfo {
  planName: string;
  amount: string;
  status: string;
  startDate: string;
  endDate: string;
  terms: string;
  paymentMethod: string;
}

interface LegacyContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  relationship: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

interface PaymentMethod {
  isPrimary: boolean;
  last4: string;
}

export const useProfileData = () => {
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    fullName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    profileImage: "",
    myReferralCode: "",
  });

  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({
    planName: "",
    amount: "",
    status: "",
    startDate: "",
    endDate: "",
    terms: "",
    paymentMethod: "",
  });

  const [legacyContacts, setLegacyContacts] = useState<LegacyContact[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(`${apiConfig.apiBaseURL}/details`, {
        headers: {
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      // Update profile data if available
      if (response.data && response.data.data) {
        const apiData = response.data.data;
        // Handle user details from the API response structure
        if (apiData.userDetails) {
          const userDetails = apiData.userDetails;

          const capitalizeFirstLetter = (str: string) =>
            str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

          const capitalizedFirstName = capitalizeFirstLetter(userDetails.firstName || "");
          const capitalizedLastName = capitalizeFirstLetter(userDetails.lastName || "");

          setUserProfile({
            fullName:
              capitalizedFirstName && capitalizedLastName
                ? `${capitalizedFirstName} ${capitalizedLastName}`
                : capitalizedFirstName || "",
            firstName: capitalizedFirstName,
            lastName: capitalizedLastName,
            email: userDetails.email || "",
            phone: userDetails.phoneNumber || "",
            address1: userDetails.address1 || "",
            address2: userDetails.address2 || "",
            city: userDetails.city || "",
            state: userDetails.state || "",
            zip: userDetails.zip || "",
            profileImage: userDetails.profileUrl || userDetails.profileImage || "",
            myReferralCode: userDetails.myReferralCode || "",
          });
        }

        // Handle subscription details from the API response structure
        if (apiData.subscriptionDetails) {
          const subscriptionDetails = apiData.subscriptionDetails;
          setSubscriptionInfo({
            planName: subscriptionDetails.subscriptionName || "",
            amount: subscriptionDetails.amountPaid
              ? `$${subscriptionDetails.amountPaid}`
              : "",
            status: subscriptionDetails.stripeSubscriptionStatus
              ? subscriptionDetails.stripeSubscriptionStatus.charAt(0).toUpperCase() + subscriptionDetails.stripeSubscriptionStatus.slice(1)
              : "",
            startDate: subscriptionDetails.currentPeriodStart
              ? new Date(
                  subscriptionDetails.currentPeriodStart
                ).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
              : "",
            endDate: subscriptionDetails.currentPeriodEnd
              ? new Date(
                  subscriptionDetails.currentPeriodEnd
                ).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
              : "",
            terms: subscriptionDetails.subscriptionType
              ? subscriptionDetails.subscriptionType.charAt(0).toUpperCase() + subscriptionDetails.subscriptionType.slice(1)
              : "",
            paymentMethod:
              subscriptionDetails.paymentMethods &&
              subscriptionDetails.paymentMethods.length > 0
                ? `**** **** **** ${
                    subscriptionDetails.paymentMethods.find(
                      (pm: PaymentMethod) => pm.isPrimary
                    )?.last4 || subscriptionDetails.paymentMethods[0]?.last4
                  }`
                : "",
          });
        }

        // Handle legacy contacts from the API response structure
        if (apiData.legacyContacts) {
          const legacyContactsData = apiData.legacyContacts;
          const contactsArray: LegacyContact[] = [];

          // Add primary legacy contact
          if (legacyContactsData.primaryLegacyContact) {
            contactsArray.push({
              id: "primary",
              name: legacyContactsData.primaryLegacyContact.name || "",
              email: legacyContactsData.primaryLegacyContact.email || "",
              phone: legacyContactsData.primaryLegacyContact.phoneNumber || "",
              relationship: legacyContactsData.primaryLegacyContact.relationship
                ? legacyContactsData.primaryLegacyContact.relationship.charAt(0).toUpperCase() + legacyContactsData.primaryLegacyContact.relationship.slice(1)
                : "",
              address1: legacyContactsData.primaryLegacyContact.address1 || "",
              address2: legacyContactsData.primaryLegacyContact.address2 || "",
              city: legacyContactsData.primaryLegacyContact.city || "",
              state: legacyContactsData.primaryLegacyContact.state || "",
              zip: legacyContactsData.primaryLegacyContact.zip || "",
            });
          }

          // Add secondary legacy contact
          if (legacyContactsData.secondaryLegacyContact) {
            contactsArray.push({
              id: "secondary",
              name: legacyContactsData.secondaryLegacyContact.name || "",
              email: legacyContactsData.secondaryLegacyContact.email || "",
              phone: legacyContactsData.secondaryLegacyContact.phoneNumber || "",
              relationship: legacyContactsData.secondaryLegacyContact.relationship
                ? legacyContactsData.secondaryLegacyContact.relationship.charAt(0).toUpperCase() + legacyContactsData.secondaryLegacyContact.relationship.slice(1)
                : "",
              address1: legacyContactsData.secondaryLegacyContact.address1 || "",
              address2: legacyContactsData.secondaryLegacyContact.address2 || "",
              city: legacyContactsData.secondaryLegacyContact.city || "",
              state: legacyContactsData.secondaryLegacyContact.state || "",
              zip: legacyContactsData.secondaryLegacyContact.zip || "",
            });
          }

          setLegacyContacts(contactsArray);
        }

        // Handle payment methods from the API response structure
        if (
          apiData.subscriptionDetails &&
          apiData.subscriptionDetails.paymentMethods
        ) {
          const paymentMethodsData = apiData.subscriptionDetails.paymentMethods;
          setPaymentMethods(paymentMethodsData);
        }

      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
      if (axios.isAxiosError(error)) {
        console.error("Request details:", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = (updates: Partial<UserProfile>) => {
    setUserProfile(prev => ({ ...prev, ...updates }));
  };

  const updateLegacyContacts = (contacts: LegacyContact[]) => {
    setLegacyContacts(contacts);
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  return {
    loading,
    userProfile,
    subscriptionInfo,
    legacyContacts,
    paymentMethods,
    updateUserProfile,
    updateLegacyContacts,
    refetchProfileData: fetchProfileData
  };
};